import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';

// Routes where back button should minimize the app rather than navigate back.
// Includes dashboard and any top-level authenticated landing pages.
const MINIMIZE_ROUTES = ['/dashboard', '/'];

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
  title = 'milkomatic-app';

  constructor(private router: Router) {}

  async ngOnInit() {
    if (Capacitor.isNativePlatform()) {
      // Fix: input[type="date"] does not open the native date picker on Android WebView
      // because the WebView intercepts the touch event before the input handles it.
      // Calling showPicker() explicitly during a touchend user-gesture forces it open.
      document.addEventListener('touchend', (e: Event) => {
        const target = e.target as HTMLInputElement;
        if (
          target &&
          target.tagName === 'INPUT' &&
          target.getAttribute('type') === 'date' &&
          !target.disabled &&
          !target.readOnly
        ) {
          if (typeof (target as any).showPicker === 'function') {
            try { (target as any).showPicker(); } catch (_) {}
          }
        }
      }, { passive: true });

      const { StatusBar, Style } = await import('@capacitor/status-bar');
      const platform = Capacitor.getPlatform();

      // Read the user's saved theme so status bar icons are correct from the very first frame.
      // Default to dark theme if nothing is saved yet.
      const savedTheme = localStorage.getItem('crm-theme') ?? 'dark';
      const isDark     = savedTheme !== 'light';
      // Style.Dark  = "Light text for dark backgrounds" = WHITE icons  → use for dark theme
      // Style.Light = "Dark text for light backgrounds" = BLACK icons → use for light theme
      const iconStyle  = isDark ? Style.Dark : Style.Light;

      if (platform === 'ios') {
        // iOS: WebView renders behind status bar; shell padding-top env(safe-area-inset-top) offsets content
        await StatusBar.setOverlaysWebView({ overlay: true });
        await StatusBar.setStyle({ style: iconStyle });
      } else {
        // Android: overlay:true so WebView fills top (same as iOS).
        // shell padding-top env(safe-area-inset-top) handles the offset.
        // Explicit setStyle() (based on saved theme) ensures correct icon color on all OEMs.
        await StatusBar.setOverlaysWebView({ overlay: true });
        await StatusBar.setStyle({ style: iconStyle });
      }

      // Handle Android hardware back button:
      // - On dashboard / root pages → minimize (don't go back to login)
      // - Everywhere else → navigate back within the app
      const { App } = await import('@capacitor/app');
      App.addListener('backButton', ({ canGoBack }) => {
        const currentUrl = this.router.url.split('?')[0]; // strip query params
        const isRootPage = MINIMIZE_ROUTES.includes(currentUrl);
        if (isRootPage || !canGoBack) {
          App.minimizeApp();
        } else {
          window.history.back();
        }
      });
    }
  }
}
